import React from 'react'
import { Link as LinkPage} from 'react-router-dom'
import "./bodyBlog.css";


function BodyBlog5() {
  return (
    <section className='section_bodyBlog'>
      <div className='container bodyBlog_main'>
        
         <div className='col-lg-6 col-md-12 col-sm-12 bodyBlog_content'>
          
        <hr />
        <p>
          Verifiable and confidential computation is no longer a futuristic concept — it's here, and it's advancing rapidly. These technologies are beginning to solve some of the key barriers preventing large enterprises and FinTech companies from embracing public blockchain infrastructure at scale. They might also enable many use cases in traditional industries such as privacy-preserving cloud computing and machine learning. At the core of this transformation are two powerful technologies:
        </p>

        <ul>
          <li><span className='bodyBlog_bold'>Fully Homomorphic Encryption (FHE)</span>: enabling computation directly on encrypted data.</li>
          <li><span className='bodyBlog_bold'>Zero-Knowledge Proofs (ZKPs)</span>: allowing provable claims without revealing the underlying data.</li>
        </ul>
         
         <p>While these technologies serve different but complementary goals and rapidly evolve, both face steep computational challenges limiting mainstream adoption. In this post, we’ll unpack the <span className='bodyBlog_bold'>core computation phases</span> of:</p>

         <ul>
          <li>
             FHE – even though many concepts and schemes exist (such as CKKS, lattice-based FHE etc.) a real progress towards efficient implementation had been currently made with TFHE (an FHE scheme implemented by Zama that allows fast homomorphic operations on Booleans and integers), that is currently widely viewed as the most promising scheme based on <span className='bodyBlog_bold'>its highly efficient bootstrapping process</span>, which is a crucial operation that refreshes ciphertexts to allow for an unlimited number of computations. This fast bootstrapping, sometimes referred to as "programmable bootstrapping," is a core advantage of TFHE and allows it to perform complex operations, including evaluating any function represented as a look-up table, without being limited by circuit depth. We focus on TFHE here.
          </li>
          <li>
             ZKPs – there is a variety of protocols and solutions, still given the current trends we focus on the most widely present ones based on RISC-V ISA witness generation and FRI/hash-based, commitment scheme.  
          </li>
         </ul>

         <p>
          We then analyze their <span className='bodyBlog_bold'>bottlenecks</span> and <span className='bodyBlog_bold'>hardware acceleration profiles, showing how CryptoCPU solves the key challenges, drastically reducing cost and latency while keeping integration seamless and avoiding any migration overhead</span>. 
         </p>

         <hr />

         <p>&#9881;&#65039; <span className='bodyBlog_bold'>Part 1: Fully Homomorphic Encryption with TFHE</span></p>

         <p><span className='bodyBlog_bold'>TFHE</span> is a fully homomorphic encryption scheme whose security relies on the computational hardness of the Learning With Errors (LWE) and Ring LWE (RLWE) assumptions, which make it infeasible to recover the secret key from ciphertexts. TFHE operates on very small plaintexts (typically 1-8 bits) and is optimized for evaluating arbitrary lookup-table (LUT) functions through <span className='bodyBlog_bold'>programmable bootstrapping (PBS)</span>. A TFHE ciphertext consists of an LWE mask vector of uniformly random integers and a body term that encodes the secret key, the message, and added noise.</p>

         <p>TFHE’s main limitations are the lack of batching (one message per ciphertext) and much larger ciphertext expansion compared to packed schemes like CKKS and BFV.</p>

         <p>&#128260; <span className='bodyBlog_bold'>Main Phases in TFHE Computation</span></p>
          
          <table className='bodyBlog_table bodyBlog_content_table'>

               <thead>
              <tr>
                <th>Phase</th>
                <th className='last_r_th'>Description</th>
              </tr>
            </thead>

              <tbody>
              <tr>
                <td><span className='bodyBlog_bold'>Encoding</span></td>
                <td className='last_r_td'>1-8 bit plaintext blocks are encoded into the torus domain via fixed-point scaling. Integer values use radix decomposition and TFHE’s carry-message format. These are simple integer/fixed-point operations that are extremely cheap computationally and are naturally executed on the CPU without requiring specialized acceleration.</td>
              </tr>
                <tr>
                <td><span className='bodyBlog_bold'>Encryption</span></td>
                <td className='last_r_td'>Encrypts encoded plaintext as LWE ciphertexts. Ciphertexts are large (tens of kilobytes) because each block corresponds to a full LWE sample. Cost is dominated by uniform random sampling and noise injection, both of which are efficiently handled on CPU.</td>
              </tr>
                <tr>
                <td><span className='bodyBlog_bold'>Evaluation</span></td>
                <td className='last_r_td'>This is the core and dominant computational phase. All high-level operations—<span className='bodyBlog_bold'>arithmetic (add/mul/div), bitwise operations, and comparisons</span> over cyphertext—are ultimately reduced to sequences of <span className='bodyBlog_bold'>programmable bootstrapping (PBS)</span> and <span className='bodyBlog_bold'>key switching</span>. PBS latency ranges from milliseconds to seconds depending on parameters. A 64 bit multiplication typically requires hundreds to thousands of PBSes; division uses iterative normalization and is the slowest arithmetic primitive.</td>
              </tr>
                <tr>
                <td><span className='bodyBlog_bold'>Decryption & Decoding</span></td>
                <td className='last_r_td'>Computes the inner product ⟨a, s⟩ by selecting and summing those entries of the ciphertext mask <span className='bodyBlog_bold'>a</span> indicated by the secret key bits <span className='bodyBlog_bold'>s</span>, and subtracts this value from <span className='bodyBlog_bold'>b</span>. This cancels the secret-key contribution and exposes the torus-encoded message plus small noise. The resulting value is then mapped back to the nearest plaintext element. These steps involve only simple integer additions/subtractions and are efficiently performed on CPU.</td>
              </tr>
              
            </tbody>

          </table>


          <hr />
        
            <p>&#128293; <span className='bodyBlog_bold'>Key Operations and Bottlenecks in TFHE particularly Evaluation Phase</span></p>

            <p>For efficient Evaluation phase, LWE ciphertexts are switched into RLWE form, i.e., from a flat integer vector to a polynomial of degree N over a 64 bit prime (Goldilocks) field. This structural transformation allows multiple LWE samples to be packed into a single polynomial, enabling polynomial multiplications required in blind rotation and key switching. Polynomial multiplications can be greatly accelerated particularly in HW using the Number Theoretic Transform (NTT).</p>


             <table className='bodyBlog_table bodyBlog_content_table'>

               <thead>
              <tr>
                <th>Component</th>
                <th>Complexity</th>
                <th className='last_r_th'>Bottleneck Description</th>
              </tr>
            </thead>

              <tbody>
              <tr>
                <td><span className='bodyBlog_bold'>Bootstrapping (PBS)</span></td>
                <td>O(n), n = LWE dimension</td>
                <td className='last_r_td'>PBS is the most expensive operation in TFHE. Its core is <span className='bodyBlog_bold'>blind rotation</span>, implemented using roughly <span className='bodyBlog_bold'>O(n) CMux operations</span>, each homomorphically selecting between two RLWE ciphertexts based on an encrypted bit. PBS evaluates <span className='bodyBlog_bold'>arbitrary functions</span> via <span className='bodyBlog_bold'>lookup tables (LUTs) encoded as torus polynomials</span>; blind rotation aligns the coefficient corresponding to the encrypted input, producing a <span className='bodyBlog_bold'>fresh LWE ciphertext</span>. Simultaneously, PBS <span className='bodyBlog_bold'>refreshes noise</span>, preventing accumulation across successive operations and enabling further homomorphic computation.</td>
              </tr>
                <tr>
                <td><span className='bodyBlog_bold'>Key Switching</span></td>
                <td>O(nℓN), ℓ = decomp level</td>
                <td className='last_r_td'>TFHE key switching transforms a LWE ciphertext under one secret key into a ciphertext under another key. For each element of the LWE vector, the value is decomposed into several digits, and each digit is used to index a precomputed lookup table of RLWE ciphertexts. These table entries are then <span className='bodyBlog_bold'>accumulated via modular integer addition</span> over all polynomial coefficients to produce the new ciphertext. Overall, the operation requires <span className='bodyBlog_bold'>one lookup and one polynomial addition</span> per digit per LWE element, giving a total of n·ℓ lookups and roughly nℓN modular integer additions, where n is the LWE dimension, ℓ is the decomposition level, and N is the RLWE polynomial degree. LUTs are large, as each entry is a full RLWE ciphertext (two polynomials of degree N), typically occupying several megabytes in total. This structure, relying entirely on <span className='bodyBlog_bold'>integer arithmetic and structured memory access</span>, makes key switching well-suited for hardware acceleration.</td>
              </tr>
                <tr>
                <td><span className='bodyBlog_bold'>Multiplication</span></td>
                <td>O(B²) PBS operations</td>
                <td className='last_r_td'>TFHE performs multiplication on radix-decomposed integers using long-multiplication logic: every pair of input blocks (B₁, B₂) produces a partial product via a PBS-evaluated LUT, yielding roughly B₁·B₂ PBS calls, with additional PBSes for carry correction. Since all high-cost work comes from these repeated PBS operations, multiplication is highly parallel across block pairs but still dominated by PBS latency, making it a strong candidate for hardware acceleration.</td>
              </tr>
                <tr>
                <td><span className='bodyBlog_bold'>Division</span></td>
                <td>O(B·d) iterations, d = bit-length of divisor</td>
                <td className='last_r_td'>Division is implemented through a restoring-style iterative algorithm, where each step performs a comparison and a conditional subtraction on encrypted values, both realized through PBS or CMux. These steps are sequentially dependent, where each iteration requires carry propagation, so a d-bit division requires B·d serial PBS operations, making division the slowest arithmetic primitive in TFHE and an especially important target for acceleration.</td>
              </tr>
               <tr>
                <td><span className='bodyBlog_bold'>Carry Propagation</span></td>
                <td>O(B) sequential, O(log B) depth parallel</td>
                <td className='last_r_td'>Carries are handled with small LUT-based PBS steps that determine whether a carry should be generated or propagated between adjacent blocks. Each block computes its sum and a carry-out bit via a PBS-evaluated truth table; that carry-out then becomes an input to the next block’s PBS introducing a serial dependence. 2B serial PBS operations are required.</td>
              </tr>
              
            </tbody>

          </table>

          <p>&#129504;  <span className='bodyBlog_bold'>Takeaway</span>: TFHE enables fine-grained control over encrypted data through programmable bootstrapping, supporting both Boolean logic and full integer arithmetic (up to 256-bit) — but at the cost of latency and throughput.</p>

          <hr />

          <p>&#129504; <span className='bodyBlog_bold'>Part 2: Zero-Knowledge Proofs with FRI/Hash-Based Schemes (zk-STARKs)</span></p>

          <p><span className='bodyBlog_bold'>FRI-based ZKPs</span> are a class of <span className='bodyBlog_bold'>transparent, post-quantum</span> ZKPs (e.g., zk-STARKs), that avoid elliptic curves and instead rely on <span className='bodyBlog_bold'>hash functions and polynomial commitment schemes</span>.</p>

          <p>&#128260; <span className='bodyBlog_bold'>Main Phases in FRI-Based ZKP Computation</span></p>

            <table className='bodyBlog_table bodyBlog_content_table'>

               <thead>
              <tr>
                <th>Phase</th>
                <th className='last_r_th'>Description</th>
              </tr>
            </thead>

              <tbody>
              <tr>
                <td><span className='bodyBlog_bold'>Witness generation (frontend)</span></td>
                <td className='last_r_td'>Trace in case of RISC-V based VMs is generated by executing the RISC-V program to produce a concrete execution trace (registers, PC, memory channels, selectors), turn each column of that trace into a polynomial over a finite field (interpolate + take a low-degree extension) and build algebraic constraint polynomials that encode instruction semantics / memory / copy rules.</td>
              </tr>
                 <tr>
                <td><span className='bodyBlog_bold'>Proving (backend)</span></td>
                <td className='last_r_td'>Combines algebraic constraint polynomials into a single composition/quotient polynomial whose low degree is what must be proven and finally runs an LDE + FRI proximity proof on that polynomial.</td>
              </tr>
                <tr>
                <td><span className='bodyBlog_bold'>Verification</span></td>
                <td className='last_r_td'>Checks Merkle proofs, performs spot checks, and ensures the polynomial degree and constraints are satisfied.</td>
              </tr>
              
            </tbody>

          </table>

          <hr />

          <p>&#128293; <span className='bodyBlog_bold'>Key Operations and Bottlenecks in FRI/Hash-Based ZKPs</span></p>

           <table className='bodyBlog_table bodyBlog_content_table'>

               <thead>
              <tr>
                <th>Component</th>
                <th>Complexity</th>
                <th className='last_r_th'>Bottleneck Description</th>
              </tr>
            </thead>

              <tbody>
              <tr>
                <td><span className='bodyBlog_bold'>Witness generation</span></td>
                <td>O(P·T·log(T)), where P is number of trace values (polynomials), and T is a trace length (degree of created polynomials)</td>
                <td className='last_r_td'>Producing and storing all elements of a trace while emulating VM ops. Interpolating trace polynomials.</td>
              </tr>
               <tr>
                <td><span className='bodyBlog_bold'>Low-Degree Extension</span></td>
                <td>O(P·n·log(n)), where P is number of polynomials, and n is degree of extended polynomials</td>
                <td className='last_r_td'>Encoding polynomials by extending them using NTT operations over rows.</td>
              </tr>
               <tr>
                <td><span className='bodyBlog_bold'>Merkle Commitments</span></td>
                <td>O(P·n·K), where P is number of polynomials, and n is number of hash computations, each requiring very large number of steps (K)</td>
                <td className='last_r_td'>Building hashes of extended polynomials is costly, especially for non-ZK friendly hashes.</td>
              </tr>
               <tr>
                <td><span className='bodyBlog_bold'>Generate Fiat-Shamir queries</span></td>
                <td>O(NQ·log(n)·K), where NQ is number of queries, n is degree of extended polynomials, and K is number of steps in hash computation</td>
                <td className='last_r_td'>Hash Merkle root to generate challenge indices. Open Merkle paths (compute hashes) at sampled positions.</td>
              </tr>
               <tr>
                <td><span className='bodyBlog_bold'>FRI Protocol</span></td>
                <td>O(n·log(n)), where n is degree of extended polynomials</td>
                <td className='last_r_td'>Recursively folds polynomial evaluations into smaller polynomials until remaining polynomial is of low-degree.</td>
              </tr>
             
              
            </tbody>

          </table>

          <p>&#129504; <span className='bodyBlog_bold'>Takeaway</span>: While proving time is significant, verification is extremely fast and succinct — making zk-STARKs ideal for scalable public verification (e.g., blockchains).</p>

          <hr />

          <p>&#9876; <span className='bodyBlog_bold'>TFHE vs FRI-ZKP: Bottlenecks Side by Side // CryptoCPU Enhancements</span></p>

           <table className='bodyBlog_table bodyBlog_content_table'>

               <thead>
              <tr>
                <th>Feature / Phase</th>
                 <th>TFHE (FHE)</th>
                <th className='last_r_th'>FRI-ZKP (ZK)</th>
              </tr>
            </thead>

              <tbody>
              <tr>
                <td><span className='bodyBlog_bold'>Most Expensive Phase</span></td>
                <td>Bootstrapping requiring 3n NTT operations per each gate</td>
                <td className='last_r_td'>Prover backend (hashing, large NTT, polynomial ops)</td>
              </tr>
              <tr>
                <td><span className='bodyBlog_bold'>Parallelism</span></td>
                <td>Gate-level parallelism possible</td>
                <td className='last_r_td'>FFTs are GPU-friendly, Merkle Tree building significantly less</td>
              </tr>
              <tr>
                <td><span className='bodyBlog_bold'>Latency Profile</span></td>
                <td>Per-operation latency high (from   ms to a couple of seconds)</td>
                <td className='last_r_td'>Very high for prover (tens of seconds to minutes); low for verifier</td>
              </tr>
              <tr>
                <td><span className='bodyBlog_bold'>Best Use Case</span></td>
                <td>Encrypted logic / decision making</td>
                <td className='last_r_td'>Auditable computation (blockchains, verifiable ML)</td>
              </tr>
              <tr>
                <td><span className='bodyBlog_bold'>Hardware Fit</span></td>
                <td>
                  <div className='cel-flex'>
                    <div>&#9989; FPGA (for low-latency bootstrapping)</div>
                    <div>&#9989; GPU (for batch PBS over gates)</div>
                  </div>
                </td>
                <td className='last_r_td'>
                   <div className='cel-flex'>
                    <div>&#9989; GPU (polynomial ops)</div>
                    <div>&#9989; FPGA (NTT, streamed hashing)</div>
                  </div>
                  </td>
              </tr>
                
            </tbody>

          </table>

          <hr />

          <p>&#129513; <span className='bodyBlog_bold'>Final Thoughts</span></p>

          <p>Both TFHE and FRI-based ZKPs represent deep cryptographic innovation. However, their architectures reflect <span className='bodyBlog_bold'>very different performance trade-offs</span>:</p>

          <ul>
            <li><span className='bodyBlog_bold'>TFHE</span> provides a full bit-level control over encrypted logic at the cost of heavy bootstrapping — well-suited for privacy-preserving control flow.</li>
            <li><span className='bodyBlog_bold'>FRI-based ZKPs</span> provide succinct, auditable proofs of large computations — ideal for verifiable computation with fast public verification.</li>
          </ul>

          <p className='p-b3-last'>As hardware acceleration improves (especially with custom silicon and GPU row computational power), it’s expected that these protocols become more practical in real-world deployments across secure AI, private computation, and decentralized systems.</p>

         
            <p className='bodyBlog_link_info bodyBlog_bold'>Read Next</p>
            <p>
                                 <LinkPage to={'/blog/cryptocpu-making-zero-knowledge-proofs-fast-cost-disruptive-and-scalable'} rel="noopener noreferrer" className='bodyBlog_link2 bodyBlog_bold'>CryptoCPU: Making Zero-Knowledge Proofs Fast, Cost-Disruptive, and Scalable</LinkPage>
                              </p>
             <p>
                <LinkPage to={'/blog/scaling-stablecoins-with-confidentiality'} rel="noopener noreferrer" className='bodyBlog_link2 bodyBlog_bold'>Scaling Stablecoins with Confidentiality: How CryptoCPU Unlocks Enterprise Adoption through ZKP+FHE</LinkPage>
                </p>
            <p>
                <LinkPage to={'/blog/cryptocpu-validation-proving-made-fast-affordable-and-seamless'} rel="noopener noreferrer" className='bodyBlog_link2 bodyBlog_bold'>CryptoCPU: validation & proving made fast, affordable and seamless</LinkPage>
                </p>
                <p>
                   <LinkPage to={'/blog/ponos-technology-s-hw-accelerated-plonky2-goldibear'} rel="noopener noreferrer" className='bodyBlog_link2 bodyBlog_bold'>Ponos Technology’s HW-Accelerated Plonky2 GoldiBear: Unleashing Plonky2 Over BabyBear Throughput</LinkPage>
                </p>
            <div>
                <LinkPage to={'/blog/commoditizing-zero-knowledge-proofs'} rel="noopener noreferrer" className='bodyBlog_link2 bodyBlog_bold'>Commoditizing Zero-Knowledge Proofs: FPGA-Driven SW-HW Co-Design as a Path to ASIC Acceleration</LinkPage>
                </div>
         
        </div>
       
      </div>
    </section>
  )
}

export default BodyBlog5